import store from '@polleverywhere/redux'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import AuthBase from '../auth_base'
import tmpl from '../templates/multi_step_login/auth_password.hamlc'

function handleAuthError (e) {
  const error = e.responseJSON?.error || (e.type ? e : {})

  this.state.set('submitting', false)

  if (error.type === 'AuthenticationFailed') {
    this.form.toggleError('password', 'Password is incorrect')
  } else {
    const message = error.message ||
      'Unable to log in, please try again or contact support@polleverywhere.com'
    this.state.set('error', message)
  }

  this.trigger('error', error, e)
}

function canUseSSO () {
  return this.userCheck.sso && this.userCheck.login_labels.length
}

function initialCta () {
  return canUseSSO.call(this) ? 'Log in with a password' : 'Log in'
}

/**
 * This component will authenticate a session based on a username that is
 * provided from an external source and a password provided from a field
 * rendered by this component.
 *
 * @event click:identity User clicked username
 *
 * @event click:auth_saml User clicked toggle to SSO
 *
 * @event error callback from authentication
 *
 * @event view:action View action events
 *   @param {String} name Event name
 *   @param {Object} data Event data
 *   @param {Object} options Event options
 *
 * @event complete Triggered when the session is authenticated
 *   @param {String} name Event name
 */

export default class AuthPassword extends AuthBase {
  get className () { return 'component-auth__multi-step-login__auth-password' }
  get cta () { return this.state.get('submitting') ? 'Logging in …' : initialCta.call(this) }
  get fields () {
    return ['multi-step-login-authenticate-username', 'password-exist']
  }

  get template () { return tmpl() }

  get events () {
    return {
      'click pe-text-field[name="username"]': () => this.trigger('click:identity'),
      [`click .${this.className}__saml`]: () => this.trigger('click:use_saml'),
      'click button[type="submit"]': 'handleSubmit'
    }
  }

  /**
   * @param {Object} options Configuration options.
   * @param {Object} options.model Session model we use for authentication, must provide 'authenticate' method
   * @param {String} options.username Prefilled read-only username
   * @param {Object} options.userCheck Account specific auth settings
   * @param {Boolean} options.redux Set true to use the redux store
   * @param {Boolean} options.upgradePresenter Passthrough to redux profileSlice.authWithPassword
   * @param {Boolean} options.includeAccount Passthrough to redux profileSlice.authWithPassword
   */
  initialize (options = {}) {
    if (!options.redux && typeof options.model !== 'object') {
      throw new TypeError('Session model must be passed in')
    }
    if (!options.username || !options.username.length) {
      throw Error('Non empty username string must be passed in')
    }
    if (typeof options.userCheck !== 'object') {
      throw new TypeError('userCheck must be passed in')
    }

    this.options = options
    this.userCheck = options.userCheck

    // Pass username back into super class' state model so it can be used in the form
    super.initialize({ username: this.options.username })
  }

  render () {
    super.render()

    this.$_element('saml').prop('hidden', !canUseSSO.call(this))

    return this
  }

  handleSubmit (e) {
    if (e) { e.preventDefault() }

    if (!this.validate()) { return }

    this.state.set('submitting', true)

    const data = this.form.serialize()

    if (this.options.redux) {
      store.dispatch(profileSlice.authWithPassword({
        login: this.options.username,
        password: data.password,
        upgradePresenter: this.options.upgradePresenter,
        includeAccount: this.options.includeAccount
      }))
        .then((data) => {
          if (data.error) {
            handleAuthError.call(this, data.payload)
          } else {
            this.trigger('complete', 'login')
          }
        })
        .catch(handleAuthError.bind(this))
    } else {
      this.model.authenticate(this.options.username, data.password)
        .then(() => this.trigger('complete', 'login'))
        .catch(handleAuthError.bind(this))
    }
  }
}
